import { useMemo } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";

import { useMatches, usePlayerList, useResults } from "@/store/selectors";
import { CardHeader, CardTitle, ChartContainer, GlassCard } from "@/ui/styled";
import { CHART_COLORS, CHART_GRID, CHART_TEXT } from "@/ui/theme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export default function RatingChart() {
  const playersList = usePlayerList();
  const results = useResults();
  const matches = useMatches();

  const data = useMemo(() => {
    const resultsList = Object.values(results);

    if (resultsList.length === 0 || playersList.length === 0) return null;

    const sortedResults = [...resultsList].sort((a, b) => {
      const matchA = matches[a.matchId];
      const matchB = matches[b.matchId];
      return (matchA?.createdAt ?? 0) - (matchB?.createdAt ?? 0);
    });

    const playerSkillHistory = new Map<string, number[]>();
    for (const p of playersList) {
      playerSkillHistory.set(p.id, []);
    }

    for (const result of sortedResults) {
      const playersInMatch = new Set<string>();
      for (const team of result.teamResults) {
        for (const pr of team.players) {
          playersInMatch.add(pr.playerId);
          if (!playerSkillHistory.has(pr.playerId)) {
            playerSkillHistory.set(pr.playerId, []);
          }
          const skill = Math.round(pr.ratingAfter.mu - 2 * pr.ratingAfter.sigma);
          playerSkillHistory.get(pr.playerId)!.push(skill);
        }
      }
      for (const p of playersList) {
        if (!playersInMatch.has(p.id)) {
          const history = playerSkillHistory.get(p.id)!;
          if (history.length > 0) {
            history.push(history[history.length - 1]!);
          }
        }
      }
    }

    const labels = sortedResults.map((_, i) => `Match ${i + 1}`);

    const datasets = playersList
      .filter((p) => (playerSkillHistory.get(p.id)?.length ?? 0) > 0)
      .map((p, i) => ({
        label: p.name,
        data: playerSkillHistory.get(p.id)!,
        borderColor: CHART_COLORS[i % CHART_COLORS.length],
        backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + "33",
        tension: 0.3,
        pointRadius: 3,
      }));

    return { labels, datasets };
  }, [results, matches, playersList]);

  if (!data) return null;

  return (
    <GlassCard>
      <CardHeader>
        <CardTitle>📈 Skill Over Time</CardTitle>
      </CardHeader>
      <motion.div
        initial={{ opacity: 0, scaleX: 0 }}
        animate={{ opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ transformOrigin: "left" }}
      >
        <ChartContainer>
          <Line
            data={data}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: "top",
                  labels: { color: CHART_TEXT },
                },
                title: { display: false },
              },
              scales: {
                x: {
                  ticks: { color: CHART_TEXT },
                  grid: { color: CHART_GRID },
                },
                y: {
                  ticks: { color: CHART_TEXT },
                  grid: { color: CHART_GRID },
                  title: {
                    display: true,
                    text: "Skill (μ - 2σ)",
                    color: CHART_TEXT,
                  },
                },
              },
            }}
          />
        </ChartContainer>
      </motion.div>
    </GlassCard>
  );
}
